import { useState, useEffect, useCallback, useReducer, useRef } from "react";
import { useSearchUser, User } from "@coherehealth/qm-api";

const PAGE_SIZE = 100;
const MAX_PAGES = 10; // Circuit breaker: 1000 users max before requiring search
const EMPTY_USERS: User[] = []; // Stable reference to avoid useEffect thrashing on [users] deps

interface UseUserManagementDataReturn {
  users: User[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  totalCount: number;
  /** True only when accumulation is complete and no error occurred */
  isComplete: boolean;
}

/**
 * Fetches all users via sequential page accumulation using useSearchUser.
 * Auth is handled automatically by the QmRestfulProvider (Bearer token injection).
 *
 * Design choice: client-side pagination because the backend (GET /user/search) has no
 * count endpoint and no sort support. User counts per opsGroup are ~300-500 (tenancy-scoped),
 * making full client-side fetch viable. If user counts grow >1000, revisit with a dedicated
 * count + sort endpoint on qm-service.
 *
 * When searchQuery is provided, delegates to server-side regex search instead.
 */
export function useUserManagementData(searchQuery?: string): UseUserManagementDataReturn {
  const [accumulated, setAccumulated] = useState<User[]>([]);
  const [offset, setOffset] = useState(0);
  const [done, setDone] = useState(false);
  const [fetchVersion, incrementFetchVersion] = useReducer((v: number) => v + 1, 0);
  // Synchronous guard: ref is always current across renders, unlike state which is captured in closures
  const doneRef = useRef(false);

  // Single declarative hook — queryParams drive what gets fetched
  const {
    data,
    loading,
    error,
    refetch: hookRefetch,
  } = useSearchUser({
    queryParams: searchQuery ? { name: searchQuery, max: PAGE_SIZE, offset: 0 } : { max: PAGE_SIZE, offset },
  });

  // Accumulate pages for the "fetch all" path (no search query)
  useEffect(() => {
    if (searchQuery || !data || doneRef.current) {
      return;
    }

    if (offset === 0) {
      // First page — replace accumulated
      setAccumulated(data);
    } else {
      // Subsequent pages — append
      setAccumulated((prev) => [...prev, ...data]);
    }

    // If we got a full page and haven't hit the ceiling, there might be more
    if (data.length >= PAGE_SIZE && offset < (MAX_PAGES - 1) * PAGE_SIZE) {
      setOffset((prev) => prev + PAGE_SIZE);
    } else {
      doneRef.current = true;
      setDone(true);
    }
    // `offset` intentionally excluded: offset changes trigger useSearchUser to refetch (via
    // queryParams), which updates `data`. Including offset here would fire the effect BEFORE
    // new data arrives, processing stale data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchQuery]);

  // Reset accumulation on refetch or when search clears
  useEffect(() => {
    setAccumulated([]);
    setOffset(0);
    setDone(false);
    doneRef.current = false;
  }, [fetchVersion, searchQuery]);

  // Clear partial data on error to avoid showing incomplete results as if they're the full set
  useEffect(() => {
    if (error && !searchQuery) {
      setAccumulated([]);
      doneRef.current = true;
      setDone(true);
    }
  }, [error, searchQuery]);

  const refetch = useCallback(() => {
    if (searchQuery) {
      hookRefetch();
    } else {
      // For fetch-all mode: reset state first, then force refetch only if offset is already 0
      // (when offset > 0, incrementFetchVersion resets offset to 0 which drives a new fetch naturally)
      incrementFetchVersion();
      if (offset === 0) {
        hookRefetch();
      }
    }
  }, [searchQuery, hookRefetch, offset]);

  // For search mode, use server response directly. Correctness relies on useSearchUser's internal
  // query keying — when queryParams change, loading goes true until the new response arrives.
  const users = searchQuery ? (loading ? EMPTY_USERS : data ?? EMPTY_USERS) : accumulated;
  // Loading = hook is fetching OR still accumulating pages (but NOT if an error stops accumulation)
  const isLoading = searchQuery ? loading : loading || (!done && !error);

  return {
    users,
    loading: isLoading,
    error: error?.message ?? null,
    refetch,
    totalCount: users.length,
    isComplete: done && !error,
  };
}
